import { useEffect, useLayoutEffect, useRef, useState } from "react"
import Spinner from "react-spinkit"

import LoadingModal from "../../Modals/LoadingModal"
import { QueryResult, QueryResults, ColumnConfig } from "../../../types"
import { removeUnderScores, timestampToDateString, toUpperCaseCustom } from "../../../utils"
import { ColorPalette } from "../../../config"
import { TableService } from "../../../services"
import { Table } from "simplygrow-ui"
import { TABLE_ROW_HEIGHT } from "../../../constants/table"

type DataQueryFilteredResultsProps = {
  tableColumnConfig: ColumnConfig[]
  queryResults: QueryResults
  fetchingResults?: boolean
  showLoader?: boolean
  loaderMessage?: string
}

const tableService = new TableService()

export const DataQueryFilteredResults = (props: DataQueryFilteredResultsProps) => {
  const tableRef = useRef<HTMLDivElement>(null)

  const [tableData, setTableData] = useState<Record<string, string>[]>([])
  const [refreshingModalOpen, setRefreshingModalOpen] = useState<boolean>(false)
  const [refreshTimestamp, setRefreshTimestamp] = useState<number>(Date.now())
  const [tableHeight, setTableHeight] = useState<number>(500)

  useEffect(() => {
    if (!props.queryResults?.results) {
      return
    }
    setRefreshingModalOpen(true)
    refreshProfilesFromCache(props.queryResults.results)
  }, [])

  useLayoutEffect(() => {
    if (tableRef.current) {
      const tableTopY = tableRef.current.getBoundingClientRect().top
      const browserWindowHeight = window.innerHeight
      setTableHeight(browserWindowHeight - tableTopY)
    }
  }, [tableRef.current])

  useEffect(() => {
    prepareTableData()
  }, [refreshTimestamp === 1])

  const refreshProfilesFromCache = async (results: QueryResult[]) => {
    setRefreshingModalOpen(false)
    setRefreshTimestamp(1)
  }

  const generateRowData = (result: QueryResult, tableColumnConfig: ColumnConfig[]) => {
    let data = { id: result.profilePk } as Record<string, any>
    tableColumnConfig.forEach((column) => {
      if (column.key === "dateActioned") {
        data[column.key] = timestampToDateString(result.dateActioned, "YYYY/MM/DD HH:mm") || "-"
      } else {
        data[column.key] = removeUnderScores(toUpperCaseCustom(result[column.key])) || "-"
      }
    })
    return data
  }

  const prepareTableData = async () => {
    if (!props.queryResults?.results) {
      return
    }

    const data = props.queryResults?.results.map((result) => generateRowData(result, props.tableColumnConfig))
    setTableData(data)
    setRefreshTimestamp(Date.now())
  }

  let refreshInProgressModal = null
  if (refreshingModalOpen) {
    refreshInProgressModal = <LoadingModal open={true}>Refreshing profiles...</LoadingModal>
  }

  const columnConfig = tableService.convertPfColumnConfigToGridColDef(props.tableColumnConfig)
  let tableContent = (
    <div ref={tableRef} style={{ width: "100%" }}>
      <Table
        rows={tableData ?? []}
        columns={columnConfig}
        tableHeight={tableHeight}
        rowHeight={TABLE_ROW_HEIGHT}
        key={`dataQueryFilteredResults_dataTable_${refreshTimestamp}`}
      />
    </div>
  )
  if (props.showLoader) {
    tableContent = (
      <div
        style={{
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}>
        <Spinner
          style={{ transform: "scale(0.6)" }}
          fadeIn="quarter"
          name="three-bounce"
          color={ColorPalette.PRIMARY_BLUE}
        />
        <h1 style={{ ...textStyle }}>{props.loaderMessage || "Fetching results"}</h1>
      </div>
    )
  }
  return (
    <>
      {tableContent}
      {refreshInProgressModal}
    </>
  )
}

const textStyle = {
  textAlign: "center" as "center",
  alignSelf: "center",
  fontFamily: "roboto",
  fontWeight: "normal",
  color: ColorPalette.SECONDARY_TEXT,
  fontSize: "0.85rem",
  paddingLeft: 10,
  paddingRight: 10,
  marginTop: 25,
}
